import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface LifestyleGalleryItem {
  id: string;
  title: string;
  category: string;
  image_url: string;
  likes: number;
  views: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface LifestyleGalleryFormData {
  title: string;
  category: string;
  image_url: string;
  likes: number;
  views: number;
  is_active: boolean;
  sort_order: number;
}

export const useLifestyleGallery = () => {
  return useQuery({
    queryKey: ['lifestyle-gallery'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lifestyle_gallery')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as LifestyleGalleryItem[];
    },
  });
};

export const useAllLifestyleGallery = () => {
  return useQuery({
    queryKey: ['all-lifestyle-gallery'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lifestyle_gallery')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as LifestyleGalleryItem[];
    },
  });
};

export const useCreateLifestyleGalleryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LifestyleGalleryFormData) => {
      const { data: result, error } = await supabase
        .from('lifestyle_gallery')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestyle-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['all-lifestyle-gallery'] });
      toast({
        title: 'Success',
        description: 'Gallery item created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create gallery item',
        variant: 'destructive',
      });
      console.error('Error creating gallery item:', error);
    },
  });
};

export const useUpdateLifestyleGalleryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LifestyleGalleryFormData> }) => {
      const { data: result, error } = await supabase
        .from('lifestyle_gallery')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestyle-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['all-lifestyle-gallery'] });
      toast({
        title: 'Success',
        description: 'Gallery item updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update gallery item',
        variant: 'destructive',
      });
      console.error('Error updating gallery item:', error);
    },
  });
};

export const useDeleteLifestyleGalleryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('lifestyle_gallery')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestyle-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['all-lifestyle-gallery'] });
      toast({
        title: 'Success',
        description: 'Gallery item deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete gallery item',
        variant: 'destructive',
      });
      console.error('Error deleting gallery item:', error);
    },
  });
};

export const useReorderLifestyleGallery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: Array<{ id: string; sort_order: number }>) => {
      const updates = items.map(item =>
        supabase
          .from('lifestyle_gallery')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw errors[0].error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestyle-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['all-lifestyle-gallery'] });
      toast({
        title: 'Success',
        description: 'Gallery items reordered successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to reorder gallery items',
        variant: 'destructive',
      });
      console.error('Error reordering gallery items:', error);
    },
  });
};